import z from "zod";
import {
  CreateShelfPayloadSchema,
  CreateShelfRowPalletPayloadSchema,
  CreateShelfRowPayloadSchema,
  UpdateManifestPayloadSchema,
  UpdateShelfBayLevelNamePayloadSchema,
  UpdateShelfNamePayloadSchema,
} from "../hooks/productManifest";
import { apiClient } from "../commonApis/apiClient";

export interface Shelf {
  id: string;
  warehouseId: string;
  location: { lat: number; lng: number };
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  storageType?: "B2C" | "B2B";
  hasExpiry?: boolean;
}

export interface Manifest {
  id: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Product {
  id: string;
  sku: string;
  itemName: string;
  hasExpiry?: boolean;
}

export interface ProductManifestResponse {
  page: number;
  productManifests: [];
  total: number;
  totalPages: number;
}
export interface ShelfRow {
  id: string;
  shelfId: string;
  warehouseId: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShelfRowBay {
  id: string;
  rowId: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShelfRowFloor {
  id: string;
  rowId: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShelfRowPallet {
  id: string;
  rowId: string;
  name?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type CreateShelfPayload = z.infer<typeof CreateShelfPayloadSchema>;

export type UpdateManifestPayload = z.infer<typeof UpdateManifestPayloadSchema>;

export type CreateShelfRowPayload = z.infer<typeof CreateShelfRowPayloadSchema>;

export interface UpdateShelfRowPayload {
  name?: string;
  isActive?: boolean;
}

export interface CreateShelfRowBayBinPayload {
  bayId: string;
  warehouseId: string;
  shelveId: string;
  rowInstanceId: string;
  clientId: string;
  storageType: "B2C" | "B2B";
  bins: {
    level: string;
    binType: string;
    binNumbers: string;
  }[];
}
export interface UpdateShelfRowBayBinPayload {
  name?: string;
  id?: string;
}

export interface AddLevelInBayPayload {
  name: string;
  bayId: string;
}

export interface CreateShelfRowFloorPayload {
  warehouseId: string;
  rowId: string;
  shelveId: string;
  rowInstanceId: string;
  createdBy: string;
  storageType: "PALLETS" | "FLOOR";
  floorSlotPrefix: string;
  floorSlotNumber: string;
}

export interface UpdateShelfRowFloorPayload {
  name?: string;
  id?: string;
}

export type CreateShelfRowPalletPayload = z.infer<
  typeof CreateShelfRowPalletPayloadSchema
>;
export type UpdateShelfRowBayPayload = z.infer<
  typeof UpdateShelfNamePayloadSchema
>;
export type UpdateShelfBayLevelNamePayload = z.infer<
  typeof UpdateShelfBayLevelNamePayloadSchema
>;

export interface UpdateShelfRowPalletPayload {
  name?: string;
  id?: string;
}

export interface BulkOperationPayload {
  ids: string[];
  isActive?: boolean;
}

export const productManifestApi = {
  // Shelf CRUD
  createManifest: (
    payload: UpdateManifestPayload | FormData,
    isFormData: boolean = false
  ) =>
    apiClient.post<Shelf>(
      "/api/product-manifest/client?type=lmClient",
      payload,
      isFormData
    ),
  getManifests: (page: number, limit: number, searchFor: string) =>
    apiClient.get<ProductManifestResponse>(
      `/api/product-manifest?page=${page}&limit=${limit}&searchFor=${searchFor}&type=lmClient`
    ),
  getAllProducts: () => apiClient.get<Product[]>(`/api/product-item/all?type=LM`),
  getManifestById: (id: string) =>
    apiClient.get<any>(`/api/product-manifest/${id}?type=lmClient`),
  updateManifest: (id: string, payload: UpdateManifestPayload) =>
    apiClient.put<Manifest>(`/api/product-manifest/${id}?type=lmClient`, payload),
  splitManifest: (id: string, payload: { selectedProductIds: string[] }) =>
    apiClient.post<{ parent: any; child: any }>(
      `/api/product-manifest/${id}/split?type=lmClient`,
      payload
    ),
  updateShelfFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<Shelf>(`/api/shelve/${id}`, payload, true),
  deleteShelf: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/${id}`, payload),
  bulkUpdateShelves: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/bulk", payload),

  // Shelf Row CRUD
  createShelfRow: (
    payload: CreateShelfRowPayload | FormData,
    isFormData: boolean = false
  ) => apiClient.post<ShelfRow>("/api/shelve/create-row", payload, isFormData),
  getShelfRows: (shelfId: string, warehouseId: string) =>
    apiClient.get<ShelfRow[]>(
      `/api/shelve/${shelfId}/rows?warehouseId=${warehouseId}&type=LM`
    ),
  getShelfRowById: (id: string) =>
    apiClient.get<ShelfRow>(`/api/shelve/row/${id}`),
  updateShelfRow: (id: string, payload: UpdateShelfRowPayload) =>
    apiClient.patch<ShelfRow>(`/api/shelve/row/${id}`, payload),
  updateShelfRowFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRow>(`/api/shelve/row/${id}`, payload, true),
  deleteShelfRow: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/${id}`, payload),
  bulkUpdateShelfRows: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/bulk", payload),

  // Update Shelf Row Bay CRUD
  updateShelfRowBay: (id: string, payload?: Record<string, any>) =>
    apiClient.patch<void>(`/api/shelve/row/rowInstance/bay/${id}`, payload),
  deleteShelfRowBay: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/bay/${id}`, payload),

  // Shelf Row Bay Level CRUD
  addShelfRowBayLevel: (id: string, payload: AddLevelInBayPayload) =>
    apiClient.put<ShelfRow>(`/api/shelve/row/rowInstance/bay/${id}`, payload),
  updateShelfRowBayLevel: (
    id: string,
    payload?: UpdateShelfBayLevelNamePayload
  ) =>
    apiClient.patch<void>(
      `/api/shelve/row/rowInstance/bay/bayLevel/${payload?.id}`,
      payload
    ),
  deleteShelfRowBayLevel: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(
      `/api/shelve/row/rowInstance/bay/bayLevel/${id}`,
      payload
    ),

  // Shelf Row Bay Bin CRUD
  createShelfRowBayBin: (
    payload: CreateShelfRowBayBinPayload | FormData,
    isFormData: boolean = false
  ) =>
    apiClient.post<ShelfRowBay>(
      "/api/shelve/create-row-bay-bin",
      payload,
      isFormData
    ),
  getShelfRowBayBins: (rowId: string) =>
    apiClient.get<ShelfRowBay[]>(`/api/shelve/row/${rowId}/bay-bins`),
  getShelfRowBayBinById: (id: string) =>
    apiClient.get<ShelfRowBay>(`/api/shelve/row/bay-bin/${id}`),
  updateShelfRowBayBin: (id: string, payload: UpdateShelfRowBayBinPayload) =>
    apiClient.patch<ShelfRowBay>(
      `/api/shelve/row/rowInstance/bay/bayLevel/bin/${id}`,
      payload
    ),
  updateShelfRowBayBinFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRowBay>(
      `/api/shelve/row/bay-bin/${id}`,
      payload,
      true
    ),
  deleteShelfRowBayBin: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(
      `/api/shelve/row/rowInstance/bay/bin/${id}`,
      payload
    ),
  bulkUpdateShelfRowBayBins: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/bay-bin/bulk", payload),

  // Shelf Row Floor CRUD
  createShelfRowFloor: (
    payload: CreateShelfRowFloorPayload | FormData,
    isFormData: boolean = false
  ) =>
    apiClient.post<ShelfRowFloor>(
      "/api/shelve/create-row-floor",
      payload,
      isFormData
    ),
  getShelfRowFloors: (rowId: string) =>
    apiClient.get<ShelfRowFloor[]>(`/api/shelve/row/${rowId}/floors`),
  getShelfRowFloorById: (id: string) =>
    apiClient.get<ShelfRowFloor>(`/api/shelve/row/floor/${id}`),
  updateShelfRowFloor: (id: string, payload: UpdateShelfRowFloorPayload) =>
    apiClient.patch<ShelfRowFloor>(
      `/api/shelve/row/rowInstance/floor/${id}`,
      payload
    ),
  updateShelfRowFloorSlot: (id: string, payload: UpdateShelfRowFloorPayload) =>
    apiClient.patch<ShelfRowFloor>(
      `/api/shelve/row/rowInstance/floor/floorSlot/${id}`,
      payload
    ),
  updateShelfRowFloorFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRowFloor>(
      `/api/shelve/row/floor/${id}`,
      payload,
      true
    ),
  deleteShelfRowFloor: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/floor/${id}`, payload),
  bulkUpdateShelfRowFloors: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/floor/bulk", payload),

  // Shelf Row Pallet CRUD
  createShelfRowPallet: (
    payload: CreateShelfRowPalletPayload | FormData,
    isFormData: boolean = false
  ) =>
    apiClient.post<ShelfRowPallet>(
      "/api/shelve/row/create-pallet",
      payload,
      isFormData
    ),
  getShelfRowPallets: (rowId: string) =>
    apiClient.get<ShelfRowPallet[]>(`/api/shelve/row/${rowId}/pallets`),
  getShelfRowPalletById: (id: string) =>
    apiClient.get<ShelfRowPallet>(`/api/shelve/row/pallet/${id}`),
  updateShelfRowPallet: (id: string, payload: UpdateShelfRowPalletPayload) =>
    apiClient.patch<ShelfRowPallet>(
      `/api/shelve/row/rowInstance/pallet/${id}`,
      payload
    ),
  updateShelfRowPalletSlot: (
    id: string,
    payload: UpdateShelfRowPalletPayload
  ) =>
    apiClient.patch<ShelfRowPallet>(
      `/api/shelve/row/rowInstance/pallet/palletSlot/${id}`,
      payload
    ),
  updateShelfRowPalletFormData: (id: string, payload: Record<string, any>) =>
    apiClient.patch<ShelfRowPallet>(
      `/api/shelve/row/pallet/${id}`,
      payload,
      true
    ),
  deleteShelfRowPallet: (id: string, payload?: Record<string, any>) =>
    apiClient.delete<void>(`/api/shelve/row/rowInstance/pallet/${id}`, payload),
  bulkUpdateShelfRowPallets: (payload: BulkOperationPayload) =>
    apiClient.patch<void>("/api/shelve/row/pallet/bulk", payload),
};
